/**
 * Componente de Gráfico de Linhas (Timeline)
 * Seguindo Single Responsibility Principle
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { HourlyStats } from '../../domain/entities/Analytics';

interface TimelineChartProps {
  data: HourlyStats;
}

/** Tooltip customizado com gênero (totais + percentuais) */
const HourlyTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;
  if (!item) return null;
  const gender = item.gender;
  const hasGender = gender && (gender.male > 0 || gender.female > 0);

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 text-sm min-w-[200px]">
      <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">
        {item.hora}
      </p>
      <p className="text-gray-600 dark:text-gray-300">
        👥 Pessoas: <span className="font-medium text-green-600">{item.pessoas?.toLocaleString('pt-BR')}</span>
      </p>
      <p className="text-gray-600 dark:text-gray-300">
        📊 Média: <span className="font-medium text-amber-600">{item.média?.toLocaleString('pt-BR')}</span>
      </p>
      {hasGender && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Gênero</p>
          <p className="text-gray-600 dark:text-gray-300">
            <span className="text-blue-600 dark:text-blue-400">♂ {gender.male.toLocaleString('pt-BR')} ({gender.male_percentage}%)</span> | <span className="text-pink-500 dark:text-pink-400">♀ {gender.female.toLocaleString('pt-BR')} ({gender.female_percentage}%)</span>
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Gráfico de Linhas para fluxo de pessoas ao longo do dia
 */
export const TimelineChart: React.FC<TimelineChartProps> = ({ data }) => {
  const chartData = data.hourly_data.map((item) => ({
    hora: `${item.hour.toString().padStart(2, '0')}:00`,
    pessoas: item.people,
    detecções: item.detections,
    média: Number(item.average_people.toFixed(2)),
    gender: item.gender,
    age_groups: item.age_groups,
  }));

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">
        Fluxo de Pessoas por Hora
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Horário de pico: {data.peak_hour.toString().padStart(2, '0')}:00 |
        Total do dia: {data.total_people} pessoas
      </p>
      <ResponsiveContainer width="100%" height={350}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis
            dataKey="hora"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={1}
            stroke="#6B7280"
            tick={{ fill: '#6B7280' }}
          />
          <YAxis 
            stroke="#6B7280"
            tick={{ fill: '#6B7280' }}
          />
          <Tooltip content={<HourlyTooltip />} />
          <Legend wrapperStyle={{ color: 'inherit' }} />
          <Line
            type="monotone"
            dataKey="pessoas"
            stroke="#10B981"
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            name="Pessoas"
          />
          <Line
            type="monotone"
            dataKey="média"
            stroke="#F59E0B"
            strokeWidth={2}
            strokeDasharray="5 5"
            name="Média"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
